<template>
	<div :class="['display-flex flex-direction-column', props.classes]">
		<div>
			<h3 :class="props.microFeedbackFormTitleClasses">{{ props.microFeedbackFormTitle }}</h3>

			<div
				v-if="props.showSubmittedState"
				class="success-container bg-color-bg-secondary mt-150 px-100 py-150 text-align-center"
			>
				<BaseSvgIcon name="hd-checkmark" height="48" width="54" />
				<p class="body-lg font-weight-bold">Thank you</p>
				<p class="mt-50">Your feedback makes the Fundrise experience even better.</p>
			</div>
			<div v-else class="display-flex flex-direction-column">
				<InputField :field="v$.selectedResponse" field-name="Feedback">
					<template #input>
						<MicroFeedbackRadios
							classes="mt-100"
							:selected-response="state.selectedResponse"
							@reaction-selected="reactionSelected"
						/>
					</template>
				</InputField>
				<div class="mt-150 mt-200-md">
					<h3 v-if="props.displayFormSubtitle" class="font-weight-bold">{{
						props.microFeedbackFormSubtitle
					}}</h3>
					<textarea
						v-model.trim="v$.additionalFeedback.$model"
						data-test="additional-feedback-textarea"
						class="input"
						:class="{ 'mt-100': props.displayFormSubtitle }"
						placeholder="Start typing (optional)"
						maxlength="1000"
						cols="100"
						:rows="props.textAreaRows"
					>
					</textarea>
				</div>
			</div>
		</div>
		<SubmitButton
			data-test="micro-feedback-submit"
			:classes="`button-default col-12-smo mt-200 ${buttonClasses}`"
			:aria-disabled="showSubmittedState"
			:is-working="isWorking"
			:disabled="showSubmittedState"
			:tabindex="showSubmittedState ? -1 : 0"
			:v$="v$"
			@sb-click="submit()"
		>
			{{ buttonText }}
		</SubmitButton>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import {
	MicroFeedbackReactionType,
	MicroFeedbackSatisfactionSubmission,
	MicroFeedbackType
} from 'types/micro-feedback';
import InputField from '@components/form/input-field.vue';
import { logError } from '@utils/error-tracking';
import MicroFeedbackRadios from '@components/micro-feedback/micro-feedback-radios.vue';
import { required } from '@vuelidate/validators';
import SubmitButton from '@components/form/submit-button.vue';
import { submitMicroFeedbackSatisfactionResponse } from '@api/micro-feedback';
import { useAppStore } from '@stores/app';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';

interface MicroFeedbackRadioFormState {
	additionalFeedback: string;
	selectedResponse: MicroFeedbackReactionType | undefined;
}

interface Props {
	associatedEventId?: string;
	microFeedbackEventType?: MicroFeedbackType;
	distinctId?: string;
	preSelectedResponse?: MicroFeedbackReactionType;
	handleRedirect?: boolean;
	classes?: string;
	microFeedbackFormTitle?: string;
	microFeedbackFormTitleClasses?: string;
	displayFormSubtitle?: boolean;
	microFeedbackFormSubtitle?: string;
	buttonClasses?: string;
	buttonText?: string;
	showSubmittedState?: boolean;
	textAreaRows?: number;
}

interface Emits {
	(e: 'feedback-form-submitted'): void;
}

const props = withDefaults(defineProps<Props>(), {
	associatedEventId: undefined,
	distinctId: '',
	preSelectedResponse: undefined,
	handleRedirect: true,
	classes: '',
	microFeedbackFormTitle: 'How would you rate your overall satisfaction with this experience?',
	microFeedbackFormTitleClasses: 'font-weight-bold',
	microFeedbackEventType: undefined,
	displayFormSubtitle: true,
	microFeedbackFormSubtitle: 'Tell us more about your rating.',
	buttonClasses: 'align-self-flex-end-md',
	buttonText: 'Submit feedback',
	showSubmittedState: false,
	textAreaRows: 1
});

const state = reactive<MicroFeedbackRadioFormState>({
	additionalFeedback: '',
	selectedResponse: undefined
});

const rules = {
	additionalFeedback: {},
	selectedResponse: {
		required
	}
};

const v$ = useVuelidate(rules, state);

const emits = defineEmits<Emits>();

const router = useRouter();

const appStore = useAppStore();

const syncingWithApi = ref(false);

const isWorking = computed((): boolean => syncingWithApi.value && !props.showSubmittedState);

function reactionSelected(reaction: MicroFeedbackReactionType): void {
	state.selectedResponse = reaction;
}

async function submitToApi(feedbackResponse: MicroFeedbackSatisfactionSubmission): Promise<void> {
	try {
		await submitMicroFeedbackSatisfactionResponse(feedbackResponse);
	} finally {
		await handlePostSubmission();
	}
}

async function handlePostSubmission(success = true) {
	if (success) {
		if (props.handleRedirect) {
			await router.push({
				name: 'micro-feedback-success',
				query: {
					eventType: props.microFeedbackEventType,
					associatedEventId: props.associatedEventId
				}
			});
		} else {
			appStore.addToast({
				type: 'success',
				message: 'Feedback submitted successfully'
			});
			emits('feedback-form-submitted');
		}
	} else {
		emits('feedback-form-submitted');
	}
}

async function submit(): Promise<void> {
	syncingWithApi.value = true;
	if (state.selectedResponse && props.distinctId) {
		if (props.microFeedbackEventType) {
			const microFeedbackResponse = {
				satisfactionResponse: state.selectedResponse,
				additionalFeedback: state.additionalFeedback ?? '',
				associatedEventId: props.associatedEventId ?? '',
				microFeedbackEventType: props.microFeedbackEventType,
				distinctId: props.distinctId
			};

			await submitToApi(microFeedbackResponse);
		} else {
			logError('Unable to determine microfeedback type');
			await handlePostSubmission(false);
		}
	}
}

function setup(): void {
	state.selectedResponse = props.preSelectedResponse ?? undefined;
}

setup();
</script>

<style lang="scss" scoped>
textarea {
	padding: 0.75rem;
	resize: vertical;
}

.success-container {
	border-radius: 0.5rem;
}

.svg-icon {
	fill: none;
}
</style>
